import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { AnimatePresence, motion, type Variants, type Transition } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useAudioNoteSavedNote } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useIsSummaryGenerating } from '@renderer/hooks/audio/use-summary-generation'
import { useCurrentMeeting } from '@renderer/hooks/useCurrentMeeting'
import { shareConversation } from '@renderer/utils/conversations/conversations'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import { useGlobalState } from '@highlight/ipc/react'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { useIsMiniAnchoredLeft } from '../side-context'
import { useViewActions, type NavigateOptions } from '@renderer/overlay/Sidepanel/view-atoms'
import { useOpenMeetingInSidepanel } from '../../../use-open-meeting-in-sidepanel'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { Button, Tooltip, setOverlayInteractive } from '@highlight/ui'
import { Link2, Check, Trash2 } from 'lucide-react'

const finishedControlsVariants: Variants = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
}

const finishedControlsTransition: Transition = { duration: 0.15 }

const AUTO_COLLAPSE_DELAY_MS = 30_000

interface MiniFinishedControlsContextValue {
  copiedShareLink?: string
  isConfirmingDelete: boolean
  isFetchingShareLink: boolean
  handleCancelDelete(event: MouseEvent): void
  handleConfirmDelete(event: MouseEvent): void
  handleDeleteClick(event: MouseEvent): void
  handleDone(event: MouseEvent): void
  handleShare(event: MouseEvent): Promise<void>
  handleView(event: MouseEvent): void
}

const MiniFinishedControlsContext = createContext<MiniFinishedControlsContextValue | null>(null)

function useMiniFinishedControls(): MiniFinishedControlsContextValue {
  const context = useContext(MiniFinishedControlsContext)
  if (!context) {
    throw new Error('Finished Mini controls must be rendered inside MiniFinishedControlsProvider')
  }
  return context
}

interface MiniFinishedControlsProviderProps {
  children: ReactNode
  /** Replaces the default 30s-after-the-summary collapse; `isHeld` pauses it, and closing the sidepanel can end it early. */
  autoCollapse?: { delayMs: number; isHeld: boolean; onSidepanelClose?: boolean }
  /** Off, nothing collapses and the controls render nothing, so a parent can stay mounted across phases. */
  isActive?: boolean
}

export function MiniFinishedControlsProvider({
  children,
  autoCollapse,
  isActive: isActiveProp = true,
}: MiniFinishedControlsProviderProps) {
  const savedNote = useAudioNoteSavedNote()
  const isActive = isActiveProp && !!savedNote
  // Key the timer on the note's stable identity, not the savedNote object reference.
  // AudioNoteStateManager rebuilds the global state object on every update (meeting
  // detection, transcript/conversation refreshes), which churns the savedNote reference.
  // Depending on the object would restart the 30s timer on each churn so it never fires.
  const savedNoteId = savedNote?.id
  const isSummaryPending = useIsSummaryGenerating(savedNoteId ?? null)
  const { currentDetectedMeeting, currentScheduledMeeting } = useCurrentMeeting()
  const { closeView, closeAllViews } = useViewActions()
  const sidepanelState = useSidepanelState()
  const openMeeting = useOpenMeetingInSidepanel()
  const [isFetchingShareLink, setIsFetchingShareLink] = useState(false)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [copiedShareLink, setCopiedShareLink] = useGlobalState<string | undefined>(
    'conversation.copiedShareLink',
    undefined,
  )

  useEffect(() => {
    if (!isActive) setIsConfirmingDelete(false)
  }, [isActive])

  // Auto-collapse timer ref — declared early so dismiss handlers can cancel it
  const autoCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const leaveFinishedNote = useCallback(
    (options?: NavigateOptions) => {
      // If no active meeting, go back to sidepanel home
      if (!currentDetectedMeeting && !currentScheduledMeeting) {
        closeAllViews(options)
      } else {
        closeView(options)
      }
    },
    [closeView, closeAllViews, currentDetectedMeeting, currentScheduledMeeting],
  )

  const handleShare = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation()
      if (!savedNote) return

      setIsFetchingShareLink(true)
      try {
        if (copiedShareLink) {
          await new Promise((resolve) => setTimeout(resolve, 250))
          await OverlayIpcRenderer.writeTextToClipboard(copiedShareLink)

          AmplitudeRenderer.trackEvent('Audio Note Action', {
            action: 'copy link',
            trigger: 'mini',
          })
        } else {
          const shareLink = await shareConversation(savedNote)

          if (shareLink) {
            await AudioNoteIpcRenderer.updateAudioNote({
              id: savedNote.id,
              updates: {
                shareLink,
              },
            })

            await OverlayIpcRenderer.writeTextToClipboard(shareLink)
            setCopiedShareLink(shareLink)
          }

          AmplitudeRenderer.trackEvent('Audio Note Action', {
            action: 'share',
            trigger: 'mini',
          })
        }
      } finally {
        setIsFetchingShareLink(false)
      }
    },
    [savedNote, copiedShareLink, setCopiedShareLink],
  )

  const handleDeleteClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    setIsConfirmingDelete(true)
  }, [])

  const handleConfirmDelete = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      if (!savedNote) return

      // Cancel auto-collapse timer to prevent double-dismiss
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current)
        autoCollapseTimerRef.current = null
      }

      AudioNoteIpcRenderer.deleteAudioNote(savedNote.id)
      AudioNoteIpcRenderer.clearAudioNoteState()
      setOverlayInteractive(false)
      leaveFinishedNote()

      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'delete',
        trigger: 'mini',
      })
    },
    [savedNote, leaveFinishedNote],
  )

  const handleCancelDelete = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    setIsConfirmingDelete(false)
  }, [])

  const handleView = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      if (!savedNote) return

      openMeeting({ mode: 'note', audioNoteId: savedNote.id, title: savedNote.title })

      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'view',
        trigger: 'mini',
      })
    },
    [savedNote, openMeeting],
  )

  const dismissMini = useCallback(
    (trigger: 'mini' | 'auto') => {
      // Cancel auto-collapse timer to prevent double-dismiss
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current)
        autoCollapseTimerRef.current = null
      }

      AudioNoteIpcRenderer.clearAudioNoteState()
      setOverlayInteractive(false)
      // The timer only collapses the Mini: an open sidepanel stays where the user is, e.g. reading this meeting's summary
      if (trigger === 'mini' || sidepanelState !== 'open') leaveFinishedNote({ automatic: trigger === 'auto' })

      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'finish',
        trigger,
      })
    },
    [leaveFinishedNote, sidepanelState],
  )

  const handleDone = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      dismissMini('mini')
    },
    [dismissMini],
  )

  // Auto-collapse after summary completes (or immediately if no summary)
  const dismissMiniRef = useRef(dismissMini)
  dismissMiniRef.current = dismissMini

  const isCollapseHeld = autoCollapse ? autoCollapse.isHeld : isSummaryPending
  const collapseDelayMs = autoCollapse?.delayMs ?? AUTO_COLLAPSE_DELAY_MS

  // A close while held (still drafting) leaves the timer to clear it, so the meeting's items still get shown
  const wasSidepanelOpenRef = useRef(sidepanelState === 'open')
  const collapsesOnSidepanelClose = !!autoCollapse?.onSidepanelClose
  useEffect(() => {
    const isOpen = sidepanelState === 'open'
    const justClosed = wasSidepanelOpenRef.current && !isOpen
    wasSidepanelOpenRef.current = isOpen
    if (justClosed && isActive && collapsesOnSidepanelClose && !isCollapseHeld && !isConfirmingDelete) {
      dismissMiniRef.current('auto')
    }
  }, [sidepanelState, isActive, collapsesOnSidepanelClose, isCollapseHeld, isConfirmingDelete])
  useEffect(() => {
    if (!isActive || isCollapseHeld || isConfirmingDelete) return

    autoCollapseTimerRef.current = setTimeout(() => {
      dismissMiniRef.current('auto')
    }, collapseDelayMs)

    return () => {
      if (autoCollapseTimerRef.current) {
        clearTimeout(autoCollapseTimerRef.current)
        autoCollapseTimerRef.current = null
      }
    }
  }, [isActive, savedNoteId, isCollapseHeld, collapseDelayMs, isConfirmingDelete])

  const value = useMemo<MiniFinishedControlsContextValue>(
    () => ({
      copiedShareLink,
      isConfirmingDelete,
      isFetchingShareLink,
      handleCancelDelete,
      handleConfirmDelete,
      handleDeleteClick,
      handleDone,
      handleShare,
      handleView,
    }),
    [
      copiedShareLink,
      isConfirmingDelete,
      isFetchingShareLink,
      handleCancelDelete,
      handleConfirmDelete,
      handleDeleteClick,
      handleDone,
      handleShare,
      handleView,
    ],
  )

  return (
    <MiniFinishedControlsContext.Provider value={isActive ? value : null}>
      {children}
    </MiniFinishedControlsContext.Provider>
  )
}

function CopyLinkButton() {
  const { copiedShareLink, handleShare, isFetchingShareLink } = useMiniFinishedControls()

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant="ghost"
            size="sm"
            shape="square"
            aria-label={copiedShareLink ? 'Copied' : 'Copy link'}
            onClick={handleShare}
            disabled={isFetchingShareLink}
          />
        }
      >
        {copiedShareLink ? <Check className="text-foreground opacity-70" /> : <Link2 strokeWidth={3} />}
      </Tooltip.Trigger>
      <Tooltip.Content sideOffset={12}>{copiedShareLink ? 'Copied' : 'Copy link'}</Tooltip.Content>
    </Tooltip>
  )
}

function DeleteButton() {
  const { handleDeleteClick } = useMiniFinishedControls()

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={<Button variant="ghost" size="sm" shape="square" aria-label="Delete" onClick={handleDeleteClick} />}
      >
        <Trash2 className="text-foreground opacity-70" fill="currentColor" />
      </Tooltip.Trigger>
      <Tooltip.Content sideOffset={12}>Delete</Tooltip.Content>
    </Tooltip>
  )
}

function ViewButton() {
  const { handleView } = useMiniFinishedControls()

  return (
    <Button variant="ghost" size="sm" onClick={handleView}>
      View
    </Button>
  )
}

function DoneButton() {
  const { handleDone } = useMiniFinishedControls()

  return (
    <Button variant="ghost" size="sm" onClick={handleDone}>
      Done
    </Button>
  )
}

interface DeleteConfirmationProps {
  reverse?: boolean
}

function DeleteConfirmation({ reverse = false }: DeleteConfirmationProps) {
  const { handleCancelDelete, handleConfirmDelete } = useMiniFinishedControls()

  return (
    <motion.div
      variants={finishedControlsVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={finishedControlsTransition}
      className={twMerge('flex items-center gap-0.5', reverse && 'flex-row-reverse')}
    >
      <Button variant="ghost" size="sm" onClick={handleCancelDelete}>
        Cancel
      </Button>
      <Button variant="primary" tone="danger" size="sm" onClick={handleConfirmDelete}>
        Delete
      </Button>
    </motion.div>
  )
}

export function MiniFinishedControlsRow() {
  const isOnLeftSide = useIsMiniAnchoredLeft()
  const controls = useContext(MiniFinishedControlsContext)
  if (!controls) {
    return null
  }
  const { isConfirmingDelete } = controls

  return (
    <div className={twMerge('flex shrink-0 items-center', isOnLeftSide ? 'pr-0.5' : 'flex-row-reverse pl-0.5')}>
      <AnimatePresence mode="wait" initial={false}>
        {isConfirmingDelete ? (
          <DeleteConfirmation key="confirm" reverse={!isOnLeftSide} />
        ) : (
          <motion.div
            key="actions"
            variants={finishedControlsVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={finishedControlsTransition}
            className={twMerge('flex items-center', !isOnLeftSide && 'flex-row-reverse')}
          >
            <Tooltip.Provider>
              <CopyLinkButton />
              <DeleteButton />
            </Tooltip.Provider>
            <ViewButton />
            <DoneButton />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function MiniFinishedControls() {
  return (
    <MiniFinishedControlsProvider>
      <MiniFinishedControlsRow />
    </MiniFinishedControlsProvider>
  )
}
